/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Alert, Button, Flex, Heading, Stack, Text} from '@chakra-ui/react'
import {AlertIcon} from '../icons'
import {noop} from '../../utils/utils'
import RegistrationFields from '../forms/registration-fields'
import Link from '../link'
import {trackEvent} from '../../tracking/analytics'
import D2BProgramLinks from '../d2b-program-links'
import {GTM_REGISTER_ERROR_MESSAGES} from '../../constants'

const RegisterForm = ({submitForm, clickSignIn = noop, form, variant}) => {
    const [submitCounter, setSubmitCounter] = useState(0)

    useEffect(() => {
        if (
            form.formState.isSubmitted &&
            Object.keys(form.formState.errors).length &&
            submitCounter != form.formState.submitCount
        ) {
            setSubmitCounter(form.formState.submitCount)
            const firstKey = Object.keys(form.formState.errors)[0]
            trackEvent({
                event: 'account_create_error',
                page_type: 'My Stokke',
                error_message: GTM_REGISTER_ERROR_MESSAGES[firstKey],
                error_type: firstKey
            })
        }
    }, [submitCounter, setSubmitCounter, form.formState.errors, form.formState])

    return (
        <Fragment>
            {variant !== 'page' && (
                <Stack justify="center" align="center" spacing={8}>
                    <Stack spacing={2}>
                        <Heading align="center" fontSize="xl" fontWeight="semibold">
                            <FormattedMessage
                                defaultMessage="Let's get started!"
                                id="register_form.heading.lets_get_started"
                            />
                        </Heading>
                        <Text fontSize="sm" align="center" color="stokkeGray.accessibleText">
                            <FormattedMessage
                                defaultMessage="Create an account and get first access to the very best products, inspiration and community."
                                id="register_form.message.create_an_account"
                            />
                        </Text>
                        <D2BProgramLinks variant={variant} />
                    </Stack>
                </Stack>
            )}
            <form
                noValidate
                id="register-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
            >
                <Stack
                    spacing={6}
                    paddingTop={{base: 4, lg: 6, xl: 8}}
                    paddingLeft={{base: 4, lg: 6, xl: 8}}
                    paddingRight={{base: 4, lg: 6, xl: 8}}
                    paddingBottom={12}
                >
                    {form.formState.errors.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    <RegistrationFields form={form} />
                    <Flex justifyContent="center">
                        <Button
                            type="submit"
                            isLoading={form.formState.isSubmitting}
                            onClick={() => form.clearErrors('global')}
                            width="100%"
                        >
                            <FormattedMessage
                                defaultMessage="Create Account"
                                id="register_form.button.create_account"
                            />
                        </Button>
                    </Flex>

                    <Stack spacing={6} alignItems="center" paddingTop={2}>
                        <Stack
                            direction="row"
                            spacing={1}
                            justify="center"
                            paddingBottom={2}
                            marginTop={4}
                        >
                            <Text fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Already have an account?"
                                    id="register_form.message.already_have_account"
                                />
                            </Text>
                            <Button variant="link" size="sm" onClick={clickSignIn}>
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="register_form.action.sign_in"
                                />
                            </Button>
                        </Stack>

                        <Text fontSize="sm" align="center">
                            <FormattedMessage
                                id="register_form.message.agree_to_policy_terms"
                                defaultMessage="By creating a Stokke account, you agree to <policy>Privacy Policy</policy> and <terms>Terms & Conditions</terms>"
                                values={{
                                    policy: (chunks) => (
                                        <Link
                                            href="/customer-service/legal/privacy-policy.html"
                                            isExternal
                                        >
                                            {chunks}
                                        </Link>
                                    ),

                                    terms: (chunks) => (
                                        <Link
                                            href="/customer-service/legal/terms/terms.html"
                                            isExternal
                                        >
                                            {chunks}
                                        </Link>
                                    )
                                }}
                            />
                        </Text>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

RegisterForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    variant: PropTypes.string
}

export default RegisterForm
